/**
 * Nimizo engraving — Shopify line item properties via Ajax Cart API.
 * Public (no leading underscore) so values appear in Admin order details / fulfillment:
 *   Engraving, Engraving Emoji, Personalized, Engraving X, Engraving Y
 * Legacy underscore keys are stripped on write and still read for older carts.
 */
(() => {
  const PROP_TEXT = 'Engraving';
  const PROP_EMOJI = 'Engraving Emoji';
  const PROP_FLAG = 'Personalized';
  const PROP_X = 'Engraving X';
  const PROP_Y = 'Engraving Y';
  // Older cart lines may still carry these — read + strip, never write
  const LEGACY_KEYS = ['_Engraving', '_Engraving Emoji', '_Personalized', 'Engraving emoji'];
  const DEFAULT_POS_X = 50;
  const DEFAULT_POS_Y = 72;
  const MAX_DEFAULT = 20;
  const EMOJIS = [
    '😊', '❤️', '⭐', '🌈', '🧸',
    '🐻', '🐰', '🦄', '🌸', '🌙',
    '☀️', '✨', '🍓', '🦋', '🐼',
  ];
  const EMOJI_SET = new Set(EMOJIS);
  const EMOJI_STRIP_VS = (s) => String(s || '').replace(/\uFE0F/g, '');
  // Control chars, bidi overrides, zero-width, line/para separators — never allowed in engraving
  const CONTROL_OR_INVISIBLE =
    /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF\uFFF9-\uFFFB]/g;
  const HTML_TAG = /<\/?[a-zA-Z][^>]*>/g;
  const ANGLE_BRACKETS = /[<>]/g;
  const CSS_JS_SMUGGLING =
    /(?:javascript|vbscript|data)\s*:|expression\s*\(|@import|url\s*\(|<\/?\s*script|on\w+\s*=/gi;
  const MULTI_SPACE = /[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+/g;

  const FOCUSABLE =
    'a[href], button:not([disabled]):not([hidden]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const qs = (root, sel) => (root || document).querySelector(sel);
  const qsa = (root, sel) => Array.from((root || document).querySelectorAll(sel));

  let lastTrigger = null;
  let activeDialog = null;
  let previewTimer = 0;
  let segmenter = null;

  function getSegmenter() {
    if (segmenter !== null) return segmenter;
    try {
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      } else {
        segmenter = false;
      }
    } catch {
      segmenter = false;
    }
    return segmenter;
  }

  function graphemeLength(str) {
    const s = String(str ?? '');
    const seg = getSegmenter();
    if (seg) return Array.from(seg.segment(s), (part) => part.segment).length;
    return Array.from(s).length;
  }

  function sliceGraphemes(str, max) {
    const s = String(str ?? '');
    const limit = Math.max(0, Number(max) || 0);
    const seg = getSegmenter();
    const parts = seg
      ? Array.from(seg.segment(s), (part) => part.segment)
      : Array.from(s);
    if (parts.length <= limit) return s;
    return parts.slice(0, limit).join('');
  }

  /**
   * Client sanitizer — defense in depth. Shopify still stores whatever is sent;
   * Liquid must sanitize again on render. Never use the result with innerHTML.
   */
  function sanitizeEngravingText(raw, max) {
    const original = String(raw ?? '');
    const hadMarkup =
      /<\/?[a-zA-Z][^>]*>/.test(original) ||
      /[<>]/.test(original) ||
      /(?:javascript|vbscript|data)\s*:/i.test(original) ||
      /\bon\w+\s*=/i.test(original) ||
      /expression\s*\(/i.test(original) ||
      /@import/i.test(original);

    let s = original;
    s = s.replace(CONTROL_OR_INVISIBLE, '');
    // Remove dangerous elements and their contents (not just the tags)
    s = s.replace(/<(script|style|iframe|object|embed|svg)\b[^>]*>[\s\S]*?<\/\1>/gi, '');
    s = s.replace(/<(script|style|iframe|object|embed|link|meta|svg)\b[^>]*\/?>/gi, '');
    s = s.replace(HTML_TAG, '');
    s = s.replace(ANGLE_BRACKETS, '');
    s = s.replace(CSS_JS_SMUGGLING, '');
    // Pure dangerous URI payloads must not survive as engraving text
    if (/^\s*(?:javascript|vbscript|data)\s*:/i.test(original.trim())) {
      s = '';
    }
    s = s.replace(MULTI_SPACE, ' ').trim();

    const strippedInvisible = original.replace(CONTROL_OR_INVISIBLE, '');
    const wasWhitespaceOnly =
      original.length > 0 &&
      s.length === 0 &&
      !hadMarkup &&
      !/[^\s\u00A0]/.test(strippedInvisible);

    const limit =
      Number.isFinite(Number(max)) && Number(max) >= 1 ? Math.floor(Number(max)) : MAX_DEFAULT;
    s = sliceGraphemes(s, limit);

    return { value: s, hadMarkup, wasWhitespaceOnly };
  }

  function sanitizeEngravingEmoji(raw) {
    const e = String(raw ?? '').replace(CONTROL_OR_INVISIBLE, '').trim();
    if (!e) return '';
    if (EMOJI_SET.has(e)) return e;
    const stripped = EMOJI_STRIP_VS(e);
    for (const allowed of EMOJIS) {
      if (EMOJI_STRIP_VS(allowed) === stripped) return allowed;
    }
    return '';
  }

  function normalizeDraft(rawText, rawEmoji, max) {
    const textResult = sanitizeEngravingText(rawText, max);
    const emoji = sanitizeEngravingEmoji(rawEmoji);
    return {
      text: textResult.value,
      emoji,
      hadMarkup: textResult.hadMarkup,
      wasWhitespaceOnly: textResult.wasWhitespaceOnly && !emoji,
    };
  }

  function formatDisplay(draft) {
    return [draft.text, draft.emoji].filter(Boolean).join(' ');
  }

  function cartApi() {
    return window.NimizoCart;
  }

  function debouncePreview(fn, wait) {
    return function debounced(...args) {
      window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function resolveMaxLength(...candidates) {
    for (const candidate of candidates) {
      const n = Number(candidate);
      if (Number.isFinite(n) && n >= 1) return Math.floor(n);
    }
    return MAX_DEFAULT;
  }

  function getDialog(context) {
    return (
      qs(document, `[data-engraving-dialog][data-engraving-context="${context}"]`) ||
      qs(document, '[data-engraving-dialog]')
    );
  }

  /** Clone <template> into document.body on first open — keeps large dialog DOM off the critical path. */
  function ensureDialog(context) {
    const existing = getDialog(context);
    if (existing) return existing;

    const tpl =
      qs(document, `template[data-engraving-modal-template="${context}"]`) ||
      qs(document, 'template[data-engraving-modal-template]');
    if (!tpl?.content) return null;

    const fragment = tpl.content.cloneNode(true);
    const dialog = qs(fragment, '[data-engraving-dialog]');
    if (!dialog) return null;

    document.body.appendChild(fragment);
    bindDialog(dialog);
    return dialog;
  }

  /**
   * Prefer the product metafield custom.engraving_image (any dedicated surface),
   * then fall back to cart thumbnail / PDP gallery only if no custom image is set.
   */
  function resolveExistingImage(trigger) {
    const host =
      trigger?.closest?.('[data-engraving-line], [data-engraving-root]') || null;
    const customSrc = host?.getAttribute?.('data-engraving-image') || host?.dataset?.engravingImage || '';
    const customAlt =
      host?.getAttribute?.('data-engraving-image-alt') || host?.dataset?.engravingImageAlt || '';
    if (customSrc) {
      return { src: customSrc, alt: customAlt };
    }

    // Legacy fallback when no dedicated engraving image metafield is set
    const lineHost =
      trigger?.closest?.('[data-cart-drawer-item], .nimizo-bag__item, [data-engraving-line]') || null;
    if (lineHost) {
      const row =
        lineHost.closest('[data-cart-drawer-item], .nimizo-bag__item') || lineHost;
      const lineImg =
        row.querySelector?.('.nimizo-cart-drawer__image, .nimizo-bag__image, .nimizo-bag__media img, img') ||
        null;
      if (lineImg && (lineImg.currentSrc || lineImg.src)) {
        return {
          src: lineImg.currentSrc || lineImg.src,
          alt: lineImg.getAttribute('alt') || '',
        };
      }
      return { src: '', alt: '' };
    }

    const pdpImg =
      document.getElementById('pdpMainImage') ||
      qs(document, '.pdp-gallery__main-image');
    if (pdpImg && (pdpImg.currentSrc || pdpImg.src)) {
      return {
        src: pdpImg.currentSrc || pdpImg.src,
        alt: pdpImg.getAttribute('alt') || '',
      };
    }

    return { src: '', alt: '' };
  }

  function resolveProductTitle(trigger) {
    const root = trigger?.closest?.('[data-engraving-root]');
    if (root) {
      const heading = qs(document, '.pdp-title, .pdp-info h1, .nimizo-pdp__title, h1.product-title');
      if (heading?.textContent) return heading.textContent.trim();
    }
    const host = trigger?.closest?.('[data-cart-drawer-item], .nimizo-bag__item');
    const titleEl = host?.querySelector?.(
      '.nimizo-cart-drawer__product, .nimizo-bag__title, a[href*="/products/"]'
    );
    return titleEl?.textContent?.trim() || '';
  }

  function getFocusable(dialog) {
    return qsa(dialog, FOCUSABLE).filter((el) => {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
      if (typeof el.checkVisibility === 'function') {
        try {
          return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
        } catch {
          /* fall through */
        }
      }
      return el.getClientRects().length > 0;
    });
  }

  function setError(dialog, message) {
    const errorEl = qs(dialog, '[data-engraving-error]');
    const textInput = qs(dialog, '[data-engraving-text]');
    if (!errorEl) return;
    if (message) {
      errorEl.hidden = false;
      errorEl.textContent = message;
      textInput?.classList.add('is-invalid');
      textInput?.setAttribute('aria-invalid', 'true');
    } else {
      errorEl.hidden = true;
      errorEl.textContent = '';
      textInput?.classList.remove('is-invalid');
      textInput?.removeAttribute('aria-invalid');
    }
  }

  function formatTooLongError(dialog, max) {
    const template =
      dialog.dataset.errorTooLongTemplate ||
      'Engraving text must be __MAX__ characters or fewer.';
    return template.replace(/__MAX__/g, String(max)).replace(/\{\{\s*count\s*\}\}/g, String(max));
  }

  function friendlyError(error) {
    if (!error) return 'Something went wrong. Please try again.';
    if (typeof error.message === 'string' && error.message) return error.message;
    return 'Shopify could not update your personalization. Please try again.';
  }

  function populateEmojiGrid(dialog) {
    const grid = qs(dialog, '[data-engraving-emoji-grid]');
    if (!grid) return;

    if (grid.dataset.ready === 'true') {
      syncEmojiSelection(dialog, dialog.dataset.draftEmoji || '');
      return;
    }

    const frag = document.createDocumentFragment();
    EMOJIS.forEach((emoji) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nimizo-engraving__emoji-btn';
      btn.textContent = emoji;
      btn.setAttribute('aria-label', `Select emoji ${emoji}`);
      btn.setAttribute('aria-pressed', 'false');
      btn.dataset.emoji = emoji;
      frag.appendChild(btn);
    });
    grid.replaceChildren(frag);
    grid.dataset.ready = 'true';

    grid.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-emoji]');
      if (!btn || !grid.contains(btn)) return;
      const emoji = btn.dataset.emoji || '';
      const current = dialog.dataset.draftEmoji || '';
      setDraft(dialog, { emoji: current === emoji ? '' : emoji }, { immediatePreview: true });
    });

    syncEmojiSelection(dialog, dialog.dataset.draftEmoji || '');
  }

  function syncEmojiSelection(dialog, selected) {
    const grid = qs(dialog, '[data-engraving-emoji-grid]');
    if (!grid) return;
    qsa(grid, '.nimizo-engraving__emoji-btn').forEach((btn) => {
      const on = btn.dataset.emoji === selected && selected !== '';
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function getDraft(dialog) {
    const textInput = qs(dialog, '[data-engraving-text]');
    const max = resolveMaxLength(dialog.dataset.engravingMax);
    const normalized = normalizeDraft(textInput?.value || '', dialog.dataset.draftEmoji || '', max);
    const pos = getMarkPosition(dialog);
    return {
      text: normalized.text,
      emoji: normalized.emoji,
      x: pos.x,
      y: pos.y,
      hadMarkup: normalized.hadMarkup,
      wasWhitespaceOnly: normalized.wasWhitespaceOnly,
    };
  }

  function clampPercent(value, fallback, min = 5, max = 95) {
    if (value == null || value === '') return fallback;
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, Math.round(n * 10) / 10));
  }

  /**
   * Engraving placement is relative to the visible custom image (not the padded frame).
   */
  function getPreviewSurface(dialog) {
    const frame = qs(dialog, '[data-engraving-preview-stage]');
    if (!frame) return null;

    const img = qs(dialog, '[data-engraving-preview-image]');
    if (img && !img.hidden && (img.currentSrc || img.src)) {
      const rect = img.getBoundingClientRect();
      if (rect.width > 1 && rect.height > 1) {
        return { frame, rect, source: 'image' };
      }
    }

    const frameRect = frame.getBoundingClientRect();
    if (!frameRect.width || !frameRect.height) return null;
    return { frame, rect: frameRect, source: 'frame' };
  }

  function pointInRect(clientX, clientY, rect) {
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }

  function getMarkPosition(dialog) {
    return {
      x: clampPercent(dialog?.dataset?.draftX, DEFAULT_POS_X, 5, 95),
      y: clampPercent(dialog?.dataset?.draftY, DEFAULT_POS_Y, 8, 92),
    };
  }

  function applyMarkPosition(dialog) {
    const mark = qs(dialog, '[data-engraving-preview-mark]');
    if (!mark) return;
    const { x, y } = getMarkPosition(dialog);
    const surface = getPreviewSurface(dialog);

    if (!surface || surface.source === 'frame') {
      mark.style.left = `${x}%`;
      mark.style.top = `${y}%`;
    } else {
      const frameRect = surface.frame.getBoundingClientRect();
      const { rect } = surface;
      const leftPx = rect.left - frameRect.left + (x / 100) * rect.width;
      const topPx = rect.top - frameRect.top + (y / 100) * rect.height;
      mark.style.left = `${leftPx}px`;
      mark.style.top = `${topPx}px`;
    }

    mark.setAttribute('aria-valuenow', String(Math.round(x)));
    mark.setAttribute('aria-valuetext', `Horizontal ${Math.round(x)}%, vertical ${Math.round(y)}%`);
  }

  function setMarkPosition(dialog, x, y) {
    if (!dialog) return;
    dialog.dataset.draftX = String(clampPercent(x, DEFAULT_POS_X, 5, 95));
    dialog.dataset.draftY = String(clampPercent(y, DEFAULT_POS_Y, 8, 92));
    applyMarkPosition(dialog);
  }

  function bindPreviewSurfaceSync(dialog) {
    if (dialog.dataset.surfaceSyncBound === 'true') return;
    dialog.dataset.surfaceSyncBound = 'true';

    const frame = qs(dialog, '[data-engraving-preview-stage]');
    const img = qs(dialog, '[data-engraving-preview-image]');

    const resync = () => {
      if (!dialog.open) return;
      applyMarkPosition(dialog);
    };

    if (typeof ResizeObserver !== 'undefined' && frame) {
      const ro = new ResizeObserver(resync);
      ro.observe(frame);
      if (img) ro.observe(img);
    }

    window.addEventListener('resize', resync);

    if (img) {
      img.addEventListener('load', resync);
    }
  }

  function bindPreviewDrag(dialog) {
    const frame = qs(dialog, '[data-engraving-preview-stage]');
    const mark = qs(dialog, '[data-engraving-preview-mark]');
    if (!frame || !mark || mark.dataset.dragBound === 'true') return;
    mark.dataset.dragBound = 'true';
    bindPreviewSurfaceSync(dialog);

    let dragging = false;
    let activePointer = null;

    const moveToPoint = (clientX, clientY) => {
      const surface = getPreviewSurface(dialog);
      if (!surface) return;
      const { rect } = surface;
      if (!rect.width || !rect.height) return;
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      setMarkPosition(dialog, x, y);
    };

    const endDrag = (event) => {
      if (!dragging) return;
      if (activePointer != null && event.pointerId !== activePointer) return;
      dragging = false;
      activePointer = null;
      mark.classList.remove('is-dragging');
      try {
        if (event.pointerId != null) mark.releasePointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    };

    mark.addEventListener('pointerdown', (event) => {
      if (event.button != null && event.button !== 0) return;
      dragging = true;
      activePointer = event.pointerId;
      mark.classList.add('is-dragging');
      try {
        mark.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
      moveToPoint(event.clientX, event.clientY);
      event.preventDefault();
    });

    mark.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== activePointer) return;
      moveToPoint(event.clientX, event.clientY);
      event.preventDefault();
    });

    mark.addEventListener('pointerup', endDrag);
    mark.addEventListener('pointercancel', endDrag);

    mark.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 4 : 1.5;
      let { x, y } = getMarkPosition(dialog);
      if (event.key === 'ArrowLeft') x -= step;
      else if (event.key === 'ArrowRight') x += step;
      else if (event.key === 'ArrowUp') y -= step;
      else if (event.key === 'ArrowDown') y += step;
      else return;
      event.preventDefault();
      setMarkPosition(dialog, x, y);
    });

    // Tap/drag only on the engraving image itself (not empty frame padding)
    frame.addEventListener('pointerdown', (event) => {
      if (event.target === mark || mark.contains(event.target)) return;
      if (event.button != null && event.button !== 0) return;
      const surface = getPreviewSurface(dialog);
      if (!surface || !pointInRect(event.clientX, event.clientY, surface.rect)) return;
      dragging = true;
      activePointer = event.pointerId;
      mark.classList.add('is-dragging');
      try {
        frame.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
      moveToPoint(event.clientX, event.clientY);
      event.preventDefault();
    });

    frame.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== activePointer) return;
      moveToPoint(event.clientX, event.clientY);
      event.preventDefault();
    });

    const endFrameDrag = (event) => {
      if (!dragging) return;
      if (activePointer != null && event.pointerId !== activePointer) return;
      dragging = false;
      activePointer = null;
      mark.classList.remove('is-dragging');
      try {
        if (event.pointerId != null) frame.releasePointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    };

    frame.addEventListener('pointerup', endFrameDrag);
    frame.addEventListener('pointercancel', endFrameDrag);
  }

  function validateDraft(dialog, draft) {
    const max = resolveMaxLength(dialog.dataset.engravingMax);
    const normalized =
      draft && typeof draft.text === 'string'
        ? normalizeDraft(draft.text, draft.emoji || '', max)
        : getDraft(dialog);

    if (normalized.hadMarkup && !normalized.text && !normalized.emoji) {
      return (
        dialog.dataset.errorInvalid ||
        'Please remove special characters or code from your engraving.'
      );
    }
    if (normalized.wasWhitespaceOnly) {
      return (
        dialog.dataset.errorWhitespace ||
        'Please enter a name or message — spaces alone are not enough.'
      );
    }
    if (graphemeLength(normalized.text) > max) {
      return formatTooLongError(dialog, max);
    }
    if (!normalized.text && !normalized.emoji) {
      return dialog.dataset.errorRequired || 'Please add a name, initials, or emoji before saving.';
    }
    return '';
  }

  function setDraft(dialog, { text, emoji, x, y } = {}, { immediatePreview = false } = {}) {
    const textInput = qs(dialog, '[data-engraving-text]');
    const max = resolveMaxLength(dialog.dataset.engravingMax);
    const normalized = normalizeDraft(
      typeof text === 'string' ? text : textInput?.value || '',
      typeof emoji === 'string' ? emoji : dialog.dataset.draftEmoji || '',
      max
    );

    if (textInput) {
      // Allow multi-code-unit graphemes; grapheme cap enforced in sanitize/getDraft
      textInput.maxLength = Math.max(max * 8, max);
      if (typeof text === 'string') {
        textInput.value = normalized.text;
      } else if (graphemeLength(textInput.value) > max) {
        textInput.value = sliceGraphemes(textInput.value, max);
      }
    }

    if (typeof emoji === 'string') {
      dialog.dataset.draftEmoji = normalized.emoji;
      syncEmojiSelection(dialog, normalized.emoji);
    }

    if (x != null || y != null) {
      const current = getMarkPosition(dialog);
      setMarkPosition(dialog, x != null ? x : current.x, y != null ? y : current.y);
    } else {
      applyMarkPosition(dialog);
    }

    setError(dialog, '');
    updateCount(dialog);
    if (immediatePreview) updatePreview(dialog);
    else schedulePreview(dialog);
  }

  function updateCount(dialog) {
    const textInput = qs(dialog, '[data-engraving-text]');
    const countEl = qs(dialog, '[data-engraving-count]');
    const remainingEl = qs(dialog, '[data-engraving-remaining]');
    const max = resolveMaxLength(dialog.dataset.engravingMax);
    if (!textInput || !countEl) return;

    const used = graphemeLength(textInput.value);
    const remaining = Math.max(0, max - used);
    const nextCount = `${used} / ${max}`;
    if (countEl.textContent !== nextCount) countEl.textContent = nextCount;
    countEl.classList.toggle('is-at-limit', used >= max);

    if (remainingEl) {
      if (!remainingEl.dataset.template) remainingEl.dataset.template = remainingEl.textContent;
      const nextRemaining = remainingEl.dataset.template
        .replace(/\{\{\s*count\s*\}\}/g, String(remaining))
        .replace(/\d+(?=\s+characters remaining)/i, String(remaining));
      if (remainingEl.textContent !== nextRemaining) remainingEl.textContent = nextRemaining;
    }
  }

  function updatePreview(dialog) {
    const draft = getDraft(dialog);
    const textEl = qs(dialog, '[data-engraving-preview-text]');
    if (!textEl) return;
    const display = formatDisplay(draft);
    if (display) {
      if (textEl.textContent !== display) textEl.textContent = display;
      delete textEl.dataset.showingPlaceholder;
      return;
    }
    const placeholder = textEl.dataset.placeholder || 'Your engraving will appear here';
    if (textEl.dataset.showingPlaceholder === 'true') return;
    textEl.replaceChildren();
    const span = document.createElement('span');
    span.className = 'nimizo-engraving__preview-placeholder';
    span.textContent = placeholder;
    textEl.appendChild(span);
    textEl.dataset.showingPlaceholder = 'true';
  }

  const schedulePreview = debouncePreview((dialog) => updatePreview(dialog), 60);

  function updatePreviewImage(dialog, { image, title, alt } = {}) {
    const img = qs(dialog, '[data-engraving-preview-image]');
    const fallback = qs(dialog, '[data-engraving-preview-fallback]');
    const caption = qs(dialog, '[data-engraving-preview-caption]');

    if (caption) {
      if (title) {
        caption.hidden = false;
        caption.textContent = title;
      } else {
        caption.hidden = true;
        caption.textContent = '';
      }
    }

    const resyncMark = () => {
      requestAnimationFrame(() => applyMarkPosition(dialog));
    };

    if (img && image) {
      // Prefer custom engraving_image metafield URL (or legacy gallery/cart fallback)
      if (img.src !== image) {
        img.addEventListener('load', resyncMark, { once: true });
        img.src = image;
      }
      img.alt = alt || title || '';
      img.hidden = false;
      if (fallback) fallback.hidden = true;
      if (img.complete) resyncMark();
    } else if (img) {
      img.removeAttribute('src');
      img.alt = '';
      img.hidden = true;
      if (fallback) fallback.hidden = false;
      resyncMark();
    }
  }

  function openDialog(context, options = {}) {
    const dialog = ensureDialog(context);
    if (!dialog || typeof dialog.showModal !== 'function') return;

    lastTrigger = options.trigger || document.activeElement;
    activeDialog = dialog;

    const max = resolveMaxLength(options.maxLength, dialog.dataset.engravingMax);
    dialog.dataset.engravingMax = String(max);
    dialog.dataset.activeLineKey = options.lineKey || '';
    dialog.dataset.activeQuantity = options.quantity != null ? String(options.quantity) : '';
    dialog.dataset.sellingPlan = options.sellingPlan || '';
    dialog.dataset.otherProperties = options.otherProperties
      ? JSON.stringify(options.otherProperties)
      : '{}';

    const removeBtn = qs(dialog, '[data-engraving-remove-modal]');
    if (removeBtn) {
      removeBtn.hidden = !(options.text || options.emoji);
    }

    const resolved = resolveExistingImage(options.trigger);
    updatePreviewImage(dialog, {
      image: options.productImage || resolved.src,
      title: options.productTitle || resolveProductTitle(options.trigger),
      alt: options.imageAlt || resolved.alt || options.productTitle || '',
    });

    populateEmojiGrid(dialog);
    bindPreviewDrag(dialog);
    setDraft(
      dialog,
      {
        text: options.text || '',
        emoji: options.emoji || '',
        x: options.x != null ? options.x : DEFAULT_POS_X,
        y: options.y != null ? options.y : DEFAULT_POS_Y,
      },
      { immediatePreview: true }
    );

    if (!dialog.open) dialog.showModal();
    requestAnimationFrame(() => {
      applyMarkPosition(dialog);
      qs(dialog, '[data-engraving-text]')?.focus();
    });
  }

  function closeDialog(dialog, { restoreFocus = true } = {}) {
    const target = dialog || activeDialog;
    if (!target?.open) return;
    target.close();
    activeDialog = null;
    if (restoreFocus && lastTrigger?.focus) lastTrigger.focus();
    lastTrigger = null;
  }

  function applyToProductForm(draft, { variantId } = {}) {
    const root = qs(document, '[data-engraving-root][data-engraving-context="product"]');
    if (!root) return;

    const max = resolveMaxLength(root.dataset.engravingMax);
    const normalized = normalizeDraft(draft?.text || '', draft?.emoji || '', max);
    const text = normalized.text;
    const emoji = normalized.emoji;
    const hasValue = Boolean(text || emoji);
    const liveVariantId =
      variantId ||
      Number(document.getElementById('selectedVariantId')?.value) ||
      Number(root.dataset.engravingBoundVariant) ||
      null;

    const textInput = qs(root, '[data-engraving-prop-text]');
    const emojiInput = qs(root, '[data-engraving-prop-emoji]');
    const flagInput = qs(root, '[data-engraving-prop-flag]');
    const xInput = qs(root, '[data-engraving-prop-x]');
    const yInput = qs(root, '[data-engraving-prop-y]');
    const saved = qs(root, '[data-engraving-saved]');
    const idle = qs(root, '[data-engraving-pdp-idle]');
    const display = qs(root, '[data-engraving-saved-display]');
    const success = qs(root, '[data-engraving-success]');
    const posX = clampPercent(draft?.x, DEFAULT_POS_X, 5, 95);
    const posY = clampPercent(draft?.y, DEFAULT_POS_Y, 8, 92);

    if (textInput) {
      textInput.value = text;
      textInput.disabled = !hasValue;
    }
    if (emojiInput) {
      emojiInput.value = emoji;
      emojiInput.disabled = !hasValue;
    }
    if (flagInput) {
      flagInput.value = hasValue ? 'Yes' : '';
      flagInput.disabled = !hasValue;
    }
    if (xInput) {
      xInput.value = hasValue ? String(posX) : '';
      xInput.disabled = !hasValue;
    }
    if (yInput) {
      yInput.value = hasValue ? String(posY) : '';
      yInput.disabled = !hasValue;
    }

    // Bind personalization to the currently selected variant only — never product id
    if (liveVariantId) {
      root.dataset.engravingBoundVariant = String(liveVariantId);
    }

    if (saved) saved.hidden = !hasValue;
    if (idle) idle.hidden = hasValue;
    if (display) display.textContent = hasValue ? formatDisplay({ text, emoji }) : '';
    if (success && hasValue) showSuccess(success);
  }

  function clearProductForm() {
    applyToProductForm({ text: '', emoji: '', x: DEFAULT_POS_X, y: DEFAULT_POS_Y });
    const root = qs(document, '[data-engraving-root][data-engraving-context="product"]');
    const success = qs(root, '[data-engraving-success]');
    if (success) success.hidden = true;
  }

  function showSuccess(el) {
    if (!el) return;
    el.hidden = false;
    el.classList.add('is-visible');
    window.setTimeout(() => {
      el.classList.remove('is-visible');
      el.hidden = true;
    }, 2400);
  }

  function stripEngravingKeys(props) {
    const next = { ...props };
    delete next[PROP_TEXT];
    delete next[PROP_EMOJI];
    delete next[PROP_FLAG];
    delete next[PROP_X];
    delete next[PROP_Y];
    LEGACY_KEYS.forEach((key) => {
      delete next[key];
    });
    return next;
  }

  function buildEngravingProperties(other, draft, { remove = false, maxLength } = {}) {
    const next = stripEngravingKeys(other || {});
    const normalized = normalizeDraft(draft?.text || '', draft?.emoji || '', maxLength || MAX_DEFAULT);

    if (remove || (!normalized.text && !normalized.emoji)) return next;

    // Stable public keys for Admin / packing slips / fulfillment apps — already sanitized
    if (normalized.text) next[PROP_TEXT] = normalized.text;
    if (normalized.emoji) next[PROP_EMOJI] = normalized.emoji;
    next[PROP_FLAG] = 'Yes';
    next[PROP_X] = String(clampPercent(draft?.x, DEFAULT_POS_X, 5, 95));
    next[PROP_Y] = String(clampPercent(draft?.y, DEFAULT_POS_Y, 8, 92));
    return next;
  }

  function readEngravingFromProperties(props, maxLength) {
    const p = props || {};
    const normalized = normalizeDraft(
      p[PROP_TEXT] || p['_Engraving'] || '',
      p[PROP_EMOJI] || p['_Engraving Emoji'] || p['Engraving emoji'] || '',
      maxLength || MAX_DEFAULT
    );
    return {
      text: normalized.text,
      emoji: normalized.emoji,
      personalized: p[PROP_FLAG] === 'Yes' || p['_Personalized'] === 'Yes' ? 'Yes' : '',
      x: clampPercent(p[PROP_X], DEFAULT_POS_X, 5, 95),
      y: clampPercent(p[PROP_Y], DEFAULT_POS_Y, 8, 92),
    };
  }

  function parseOtherProperties(lineEl) {
    const script = qs(lineEl, '[data-engraving-other-properties]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}') || {};
    } catch {
      return {};
    }
  }

  function findCartItemByKey(cart, key) {
    return (cart?.items || []).find((item) => item.key === key) || null;
  }

  function readLiveLineQuantity(lineKey, fallback) {
    const row =
      qs(document, `[data-cart-drawer-item][data-line-key="${CSS.escape(lineKey)}"]`) ||
      qs(document, `.nimizo-bag__item[data-key="${CSS.escape(lineKey)}"]`);
    if (row?.dataset?.quantity) {
      const n = Number(row.dataset.quantity);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    const qtyInput = qs(document, `.nimizo-bag__qty-input[data-line-key="${CSS.escape(lineKey)}"]`);
    if (qtyInput) {
      const n = Number(qtyInput.value);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    const line = qs(document, `[data-engraving-line][data-line-key="${CSS.escape(lineKey)}"]`);
    if (line?.dataset?.lineQuantity) {
      const n = Number(line.dataset.lineQuantity);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return Number(fallback) || 1;
  }

  function remapLineKeys(oldKey, newKey) {
    if (!oldKey || !newKey || oldKey === newKey) return;
    const selectors = [
      `[data-engraving-line][data-line-key="${CSS.escape(oldKey)}"]`,
      `[data-cart-drawer-item][data-line-key="${CSS.escape(oldKey)}"]`,
      `.nimizo-bag__item[data-key="${CSS.escape(oldKey)}"]`,
      `.nimizo-bag__qty-input[data-line-key="${CSS.escape(oldKey)}"]`,
      `[data-cart-drawer-qty][data-line-key="${CSS.escape(oldKey)}"]`,
      `[data-cart-drawer-remove][data-line-key="${CSS.escape(oldKey)}"]`,
    ];
    selectors.forEach((sel) => {
      qsa(document, sel).forEach((el) => {
        if (el.dataset.lineKey !== undefined) el.dataset.lineKey = newKey;
        if (el.dataset.key !== undefined) el.dataset.key = newKey;
        if (el.getAttribute('data-line-key')) el.setAttribute('data-line-key', newKey);
        if (el.getAttribute('data-key')) el.setAttribute('data-key', newKey);
        if (el.name && el.name.startsWith('updates[')) {
          el.name = `updates[${newKey}]`;
        }
      });
    });
  }

  function resolveLineLabels(lineEl) {
    return {
      add: lineEl?.dataset?.labelAdd || 'Add engraving',
      edit: lineEl?.dataset?.labelEdit || 'Edit engraving',
      remove: lineEl?.dataset?.labelRemove || 'Remove engraving',
      saved: lineEl?.dataset?.labelSaved || 'Personalization saved',
      cartLabel: lineEl?.dataset?.labelCart || 'Engraving:',
    };
  }

  function appendAddEngravingButton(lineEl) {
    const labels = resolveLineLabels(lineEl);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nimizo-engraving__trigger nimizo-engraving__trigger--compact';
    btn.dataset.engravingOpen = '';
    btn.dataset.engravingMode = 'add';
    const icon = document.createElement('span');
    icon.className = 'nimizo-engraving__trigger-icon';
    icon.setAttribute('aria-hidden', 'true');
    const iconGlyph = document.createElement('i');
    iconGlyph.className = 'hgi hgi-stroke hgi-pencil-edit-02 nimizo-icon nimizo-icon--sm';
    iconGlyph.setAttribute('aria-hidden', 'true');
    icon.appendChild(iconGlyph);
    const label = document.createElement('span');
    label.className = 'nimizo-engraving__trigger-label';
    label.textContent = labels.add;
    btn.append(icon, label);
    lineEl.appendChild(btn);
  }

  function buildLineSummaryDom(lineEl) {
    const labels = resolveLineLabels(lineEl);
    const summary = document.createElement('div');
    summary.className = 'nimizo-engraving__line-summary';
    summary.dataset.engravingLineSummary = '';

    const value = document.createElement('p');
    value.className = 'nimizo-engraving__line-value';
    const label = document.createElement('span');
    label.className = 'nimizo-engraving__line-label';
    label.textContent = labels.cartLabel;
    const display = document.createElement('span');
    display.dataset.engravingLineDisplay = '';
    display.dir = 'auto';
    value.append(label, document.createTextNode(' '), display);

    const success = document.createElement('span');
    success.className = 'nimizo-engraving__success';
    success.dataset.engravingSuccess = '';
    success.hidden = true;
    success.textContent = labels.saved;

    summary.append(value, success);
    return summary;
  }

  function buildLineActionsDom(lineEl) {
    const labels = resolveLineLabels(lineEl);
    const actions = document.createElement('div');
    actions.className = 'nimizo-engraving__line-actions';

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'nimizo-engraving__line-btn';
    edit.dataset.engravingOpen = '';
    edit.dataset.engravingMode = 'edit';
    edit.textContent = labels.edit;

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'nimizo-engraving__line-btn nimizo-engraving__line-btn--danger';
    remove.dataset.engravingRemove = '';
    remove.textContent = labels.remove;

    actions.append(edit, remove);
    return actions;
  }

  function updateCartPageLine(cart, lineKey, draft, { removed = false } = {}) {
    const lineEl = qs(document, `[data-engraving-line][data-line-key="${CSS.escape(lineKey)}"]`);
    if (!lineEl) return;

    const item = findCartItemByKey(cart, lineKey);
    if (item) {
      lineEl.dataset.lineQuantity = String(item.quantity);
      const qtyInput = qs(document, `.nimizo-bag__qty-input[data-line-key="${CSS.escape(lineKey)}"]`);
      if (qtyInput) qtyInput.value = String(item.quantity);
    }

    const max = resolveMaxLength(lineEl.dataset.engravingMax);
    const normalized = normalizeDraft(draft?.text || '', draft?.emoji || '', max);

    if (removed || (!normalized.text && !normalized.emoji)) {
      qs(lineEl, '[data-engraving-line-summary]')?.remove();
      qs(lineEl, '.nimizo-engraving__line-actions')?.remove();
      lineEl.dataset.engravingText = '';
      lineEl.dataset.engravingEmoji = '';
      if (!qs(lineEl, '[data-engraving-open]') && lineEl.dataset.engravingEnabled === 'true') {
        appendAddEngravingButton(lineEl);
      }
      return;
    }

    lineEl.dataset.engravingText = normalized.text;
    lineEl.dataset.engravingEmoji = normalized.emoji;

    let displayEl = qs(lineEl, '[data-engraving-line-display]');
    if (!displayEl) {
      lineEl.querySelector('[data-engraving-open][data-engraving-mode="add"]')?.remove();
      const summary = buildLineSummaryDom(lineEl);
      const actions = buildLineActionsDom(lineEl);
      lineEl.prepend(actions);
      lineEl.prepend(summary);
      displayEl = qs(lineEl, '[data-engraving-line-display]');
    }

    if (displayEl) displayEl.textContent = formatDisplay(normalized);
    showSuccess(qs(lineEl, '[data-engraving-success]'));

    const bagSubtotals = qsa(document, '[data-bag-subtotal], [data-bag-total]');
    if (cart && bagSubtotals.length) {
      const money = cart.items_subtotal_price ?? cart.total_price;
      if (window.Shopify?.formatMoney && money != null) {
        const formatted = window.Shopify.formatMoney(money, window.theme?.moneyFormat || undefined);
        bagSubtotals.forEach((el) => {
          el.textContent = formatted;
        });
      }
    }
  }

  async function persistCartEngraving({
    lineKey,
    quantity,
    other,
    draft,
    remove = false,
    sellingPlan,
    maxLength,
  }) {
    const api = cartApi();
    if (!api) throw new Error('Cart is not ready. Please refresh the page.');

    const safeKey = assertLineKey(lineKey);
    const max = resolveMaxLength(maxLength, MAX_DEFAULT);
    const cartBefore = await api.getCart();
    const beforeItem = findCartItemByKey(cartBefore, safeKey);
    const variantId = beforeItem?.variant_id || null;
    const beforeKeys = new Set((cartBefore.items || []).map((item) => item.key));

    const liveQty = readLiveLineQuantity(safeKey, quantity);
    const properties = buildEngravingProperties(other, draft || {}, {
      remove,
      maxLength: max,
    });
    const payload = {
      id: safeKey,
      quantity: liveQty,
      properties,
    };
    if (sellingPlan) payload.selling_plan = sellingPlan;

    const cart = await api.changeLine(payload);

    let matched = findCartItemByKey(cart, safeKey);
    if (!matched) {
      const newcomers = (cart.items || []).filter((item) => !beforeKeys.has(item.key));
      matched =
        newcomers.find((item) => {
          if (variantId && item.variant_id !== variantId) return false;
          if (remove) return !readEngravingFromProperties(item.properties, max).text;
          const eng = readEngravingFromProperties(item.properties, max);
          return (
            eng.text === (draft?.text || '') &&
            eng.emoji === (draft?.emoji || '') &&
            (eng.personalized === 'Yes' || Boolean(eng.text || eng.emoji))
          );
        }) ||
        (!remove &&
          (cart.items || []).find((item) => {
            if (variantId && item.variant_id !== variantId) return false;
            const eng = readEngravingFromProperties(item.properties, max);
            return eng.text === (draft?.text || '') && eng.emoji === (draft?.emoji || '');
          })) ||
        null;
    }

    const activeKey = matched?.key || safeKey;
    if (activeKey !== safeKey) {
      remapLineKeys(safeKey, activeKey);
    }

    const drawerOpen = document.getElementById('NimizoCartDrawer')?.classList.contains('is-open');
    await api.onCartUpdated(cart, { openDrawer: drawerOpen });

    // After drawer refresh, cart page nodes may still need a local patch
    updateCartPageLine(cart, activeKey, draft || {}, { removed: remove });
    return cart;
  }

  function onKeydownTrap(event) {
    if (!activeDialog?.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog(activeDialog);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(activeDialog);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setSaving(dialog, on) {
    if (dialog) dialog.dataset.saving = on ? 'true' : 'false';
    dialog?.classList.toggle('is-saving', on);
    qsa(dialog, '[data-engraving-save], [data-engraving-remove-modal]').forEach((btn) => {
      btn.disabled = on;
    });
  }

  function onOpenClick(event) {
    const btn = event.target.closest('[data-engraving-open]');
    if (!btn) return;

    const line = btn.closest('[data-engraving-line]');
    if (line) {
      const mode = btn.dataset.engravingMode || 'edit';
      const hasValue = Boolean(line.dataset.engravingText || line.dataset.engravingEmoji);
      // Allow edit/remove flows even if metafield later disabled; block only brand-new adds
      if (mode === 'add' && line.dataset.engravingEnabled === 'false') return;
      if (mode !== 'add' && !hasValue && line.dataset.engravingEnabled === 'false') return;
      event.preventDefault();
      openDialog('cart', {
        trigger: btn,
        lineKey: line.dataset.lineKey,
        quantity: readLiveLineQuantity(line.dataset.lineKey, line.dataset.lineQuantity),
        text: line.dataset.engravingText || '',
        emoji: line.dataset.engravingEmoji || '',
        x: line.dataset.engravingX,
        y: line.dataset.engravingY,
        maxLength: line.dataset.engravingMax,
        productImage: line.dataset.engravingImage || '',
        imageAlt: line.dataset.engravingImageAlt || '',
        otherProperties: parseOtherProperties(line),
        sellingPlan: line.dataset.sellingPlan || '',
      });
      return;
    }

    const root = btn.closest('[data-engraving-root]');
    if (root) {
      event.preventDefault();
      openDialog(root.dataset.engravingContext || 'product', {
        trigger: btn,
        text: qs(root, '[data-engraving-prop-text]')?.value || '',
        emoji: qs(root, '[data-engraving-prop-emoji]')?.value || '',
        x: qs(root, '[data-engraving-prop-x]')?.value,
        y: qs(root, '[data-engraving-prop-y]')?.value,
        maxLength: root.dataset.engravingMax,
        productImage: root.dataset.engravingImage || '',
        imageAlt: root.dataset.engravingImageAlt || '',
      });
    }
  }

  async function onRemoveClick(event) {
    const btn = event.target.closest('[data-engraving-remove]');
    if (!btn) return;
    event.preventDefault();
    const line = btn.closest('[data-engraving-line]');
    if (!line) return;
    if (line.dataset.saving === 'true') return;
    line.dataset.saving = 'true';
    btn.disabled = true;

    try {
      await persistCartEngraving({
        lineKey: line.dataset.lineKey,
        quantity: readLiveLineQuantity(line.dataset.lineKey, line.dataset.lineQuantity || 1),
        other: parseOtherProperties(line),
        remove: true,
        sellingPlan: line.dataset.sellingPlan || '',
        maxLength: line.dataset.engravingMax,
      });
    } catch (error) {
      console.error(error);
      window.alert(friendlyError(error));
    } finally {
      line.dataset.saving = 'false';
      btn.disabled = false;
    }
  }

  function bindDialog(dialog) {
    if (!dialog || dialog.dataset.bound === 'true') return;
    dialog.dataset.bound = 'true';

    const textInput = qs(dialog, '[data-engraving-text]');
    const placeholder = qs(dialog, '.nimizo-engraving__preview-placeholder');
    if (placeholder && qs(dialog, '[data-engraving-preview-text]')) {
      qs(dialog, '[data-engraving-preview-text]').dataset.placeholder = placeholder.textContent;
    }

    textInput?.addEventListener('input', () => {
      const max = resolveMaxLength(dialog.dataset.engravingMax);
      textInput.maxLength = Math.max(max * 8, max);
      let next = textInput.value.replace(CONTROL_OR_INVISIBLE, '');
      if (graphemeLength(next) > max) next = sliceGraphemes(next, max);
      if (next !== textInput.value) textInput.value = next;
      setError(dialog, '');
      updateCount(dialog);
      schedulePreview(dialog);
    });

    qs(dialog, '[data-engraving-close]')?.addEventListener('click', () => closeDialog(dialog));
    qs(dialog, '[data-engraving-cancel]')?.addEventListener('click', () => closeDialog(dialog));

    qs(dialog, '[data-engraving-remove-modal]')?.addEventListener('click', async () => {
      if (dialog.dataset.saving === 'true') return;
      const lineKey = dialog.dataset.activeLineKey;
      if (lineKey) {
        setSaving(dialog, true);
        try {
          await persistCartEngraving({
            lineKey,
            quantity: readLiveLineQuantity(lineKey, dialog.dataset.activeQuantity || 1),
            other: JSON.parse(dialog.dataset.otherProperties || '{}'),
            remove: true,
            sellingPlan: dialog.dataset.sellingPlan || '',
            maxLength: dialog.dataset.engravingMax,
          });
          closeDialog(dialog);
        } catch (error) {
          console.error(error);
          setError(dialog, friendlyError(error));
        } finally {
          setSaving(dialog, false);
        }
        return;
      }
      clearProductForm();
      closeDialog(dialog);
    });

    qs(dialog, '[data-engraving-save]')?.addEventListener('click', async () => {
      if (dialog.dataset.saving === 'true') return;
      const draft = getDraft(dialog);
      const error = validateDraft(dialog, draft);
      if (error) {
        setError(dialog, error);
        textInput?.focus();
        return;
      }

      const lineKey = dialog.dataset.activeLineKey;
      if (dialog.dataset.engravingContext === 'cart' && lineKey) {
        setSaving(dialog, true);
        try {
          await persistCartEngraving({
            lineKey,
            quantity: readLiveLineQuantity(lineKey, dialog.dataset.activeQuantity || 1),
            other: JSON.parse(dialog.dataset.otherProperties || '{}'),
            draft,
            sellingPlan: dialog.dataset.sellingPlan || '',
            maxLength: dialog.dataset.engravingMax,
          });
          closeDialog(dialog);
        } catch (err) {
          console.error(err);
          setError(dialog, friendlyError(err));
        } finally {
          setSaving(dialog, false);
        }
        return;
      }

      applyToProductForm(draft, {
        variantId: getLiveVariantId(document.getElementById('nimizoProductForm')),
      });
      closeDialog(dialog);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener('close', () => {
      if (activeDialog === dialog) activeDialog = null;
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
  }

  function onClearSavedClick(event) {
    const btn = event.target.closest('[data-engraving-clear-saved]');
    if (!btn) return;
    event.preventDefault();
    clearProductForm();
  }

  function hasActiveEngraving(root) {
    if (!root) return false;
    const max = resolveMaxLength(root.dataset.engravingMax);
    const text = qs(root, '[data-engraving-prop-text]');
    const emoji = qs(root, '[data-engraving-prop-emoji]');
    if (text?.disabled && emoji?.disabled) return false;
    const normalized = normalizeDraft(
      text && !text.disabled ? text.value : '',
      emoji && !emoji.disabled ? emoji.value : '',
      max
    );
    return Boolean(normalized.text || normalized.emoji);
  }

  function collectFormProperties(form, root) {
    const properties = {};
    if (root && hasActiveEngraving(root)) {
      const max = resolveMaxLength(root.dataset.engravingMax);
      const text = qs(root, '[data-engraving-prop-text]');
      const emoji = qs(root, '[data-engraving-prop-emoji]');
      const xInput = qs(root, '[data-engraving-prop-x]');
      const yInput = qs(root, '[data-engraving-prop-y]');
      const normalized = normalizeDraft(
        text && !text.disabled ? text.value : '',
        emoji && !emoji.disabled ? emoji.value : '',
        max
      );
      if (normalized.text) properties[PROP_TEXT] = normalized.text;
      if (normalized.emoji) properties[PROP_EMOJI] = normalized.emoji;
      if (normalized.text || normalized.emoji) {
        properties[PROP_FLAG] = 'Yes';
        properties[PROP_X] = String(
          clampPercent(xInput && !xInput.disabled ? xInput.value : DEFAULT_POS_X, DEFAULT_POS_X, 5, 95)
        );
        properties[PROP_Y] = String(
          clampPercent(yInput && !yInput.disabled ? yInput.value : DEFAULT_POS_Y, DEFAULT_POS_Y, 8, 92)
        );
      }
    }

    form.querySelectorAll('[name^="properties["]').forEach((input) => {
      if (input.disabled) return;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      const key = match[1];
      if (key === PROP_TEXT || key === PROP_EMOJI || key === PROP_FLAG || key === PROP_X || key === PROP_Y) {
        return;
      }
      if (LEGACY_KEYS.includes(key)) return;
      if (input.value !== '') properties[key] = input.value;
    });

    return properties;
  }

  function bindVariantChange() {
    document.addEventListener('nimizo:variant:change', (event) => {
      const root = qs(document, '[data-engraving-root][data-engraving-context="product"]');
      if (!root) return;

      const nextId = Number(event.detail?.variantId);
      if (!nextId) return;

      const previousBound = Number(root.dataset.engravingBoundVariant || 0);
      const hasDraft = hasActiveEngraving(root);

      // Re-bind the current draft to the newly selected variant.
      // Never keep a stale variant id that could attach engraving to the wrong line.
      root.dataset.engravingBoundVariant = String(nextId);

      if (hasDraft && previousBound && previousBound !== nextId) {
        // Draft text is preserved for convenience, but ownership moves to the new variant.
        root.dataset.engravingRebound = 'true';
      }
    });
  }

  function getLiveVariantId(form) {
    const fromForm = Number(form?.querySelector?.('[name="id"]')?.value);
    if (fromForm) return fromForm;
    const fromGlobal = Number(document.getElementById('selectedVariantId')?.value);
    if (fromGlobal) return fromGlobal;
    return null;
  }

  function setAtcButtonState(btn, state) {
    if (!btn) return;
    const next = state || 'idle';
    btn.dataset.atcState = next;
    btn.disabled = next === 'loading';
    btn.setAttribute('aria-busy', next === 'loading' ? 'true' : 'false');

    const idle = btn.querySelector('.pdp-atc__face--idle');
    const loading = btn.querySelector('.pdp-atc__face--loading');
    const success = btn.querySelector('.pdp-atc__face--success');
    if (idle) idle.setAttribute('aria-hidden', next === 'idle' ? 'false' : 'true');
    if (loading) loading.setAttribute('aria-hidden', next === 'loading' ? 'false' : 'true');
    if (success) success.setAttribute('aria-hidden', next === 'success' ? 'false' : 'true');
  }

  async function runAddToCart(btn, action) {
    setAtcButtonState(btn, 'loading');
    try {
      await action();
      setAtcButtonState(btn, 'success');
      window.setTimeout(() => setAtcButtonState(btn, 'idle'), 1200);
    } catch (error) {
      console.error(error);
      setAtcButtonState(btn, 'idle');
      window.alert(friendlyError(error));
    }
  }

  function interceptProductForm() {
    const form = document.getElementById('nimizoProductForm');
    if (!form) return;
    const root = qs(document, '[data-engraving-root][data-engraving-context="product"]');

    form.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      const submitName = submitter?.getAttribute?.('name') || '';
      const isAccelerated =
        submitName === 'checkout' || Boolean(submitter?.closest?.('.shopify-payment-button'));

      const required = root?.dataset.engravingRequired === 'true';
      const engraved = hasActiveEngraving(root);

      // Required personalization blocks ATC and dynamic checkout alike
      if (root && required && !engraved) {
        event.preventDefault();
        openDialog('product', {
          trigger: qs(root, '[data-engraving-open]'),
          maxLength: root.dataset.engravingMax,
          productImage: root.dataset.engravingImage || '',
          imageAlt: root.dataset.engravingImageAlt || '',
        });
        const dialog = ensureDialog('product');
        if (dialog) {
          setError(
            dialog,
            dialog.dataset.errorRequired || 'Please add personalization before adding to cart.'
          );
        }
        return;
      }

      // Let Shopify accelerated checkout proceed when engraving is optional or already set
      // (hidden property inputs carry values on native checkout submit)
      if (isAccelerated) return;

      const api = cartApi();
      const liveVariantId = getLiveVariantId(form);

      // Without engraving: still use Ajax ATC when cart helper is available (consistent drawer UX)
      if (!engraved) {
        if (!api) return;
        event.preventDefault();
        if (!liveVariantId) {
          window.alert('Please select a product option.');
          return;
        }
        const qtyInput = form.querySelector('[name="quantity"]');
        const sellingPlanInput = form.querySelector('[name="selling_plan"]');
        const offers = window.NimizoPdpOffers;
        const bundles = offers?.selectedBundles?.() || [];

        const mainItem = {
          id: liveVariantId,
          quantity: Number(qtyInput?.value || 1) || 1,
          properties: collectFormProperties(form, root),
        };
        if (sellingPlanInput?.value) {
          mainItem.selling_plan = Number(sellingPlanInput.value) || sellingPlanInput.value;
        }

        const payload =
          bundles.length > 0
            ? { items: [mainItem, ...bundles] }
            : mainItem;

        const submitBtn = form.querySelector('[type="submit"][name="add"], #pdpAddToCartBtn');
        await runAddToCart(submitBtn, async () => {
          const code = offers?.selectedTierDiscountCode?.() || '';
          if (offers?.applyDiscountCode) {
            await offers.applyDiscountCode(code);
          }
          const cart = await api.addItem(payload);
          // Re-apply after add so drawer totals include the code
          if (code && offers?.applyDiscountCode) {
            await offers.applyDiscountCode(code);
          }
          const fresh = code && api.getCart ? await api.getCart() : cart;
          await api.onCartUpdated(fresh || cart, { openDrawer: true });
        });
        return;
      }

      // Engraving present: Ajax Cart API so properties attach to THIS variant only
      if (!api) return;

      event.preventDefault();

      if (!liveVariantId) {
        window.alert('Please select a product option.');
        return;
      }

      root.dataset.engravingBoundVariant = String(liveVariantId);

      const qtyInputEngraved = form.querySelector('[name="quantity"]');
      const sellingPlanInputEngraved = form.querySelector('[name="selling_plan"]');
      const quantity = Number(qtyInputEngraved?.value || 1) || 1;
      const properties = collectFormProperties(form, root);
      const offersEngraved = window.NimizoPdpOffers;
      const bundlesEngraved = offersEngraved?.selectedBundles?.() || [];

      const engravedPayload = {
        id: liveVariantId,
        quantity,
        properties,
      };
      if (sellingPlanInputEngraved?.value) {
        engravedPayload.selling_plan = Number(sellingPlanInputEngraved.value) || sellingPlanInputEngraved.value;
      }

      const engravedSubmitBtn = form.querySelector('[type="submit"][name="add"], #pdpAddToCartBtn');
      await runAddToCart(engravedSubmitBtn, async () => {
        const code = offersEngraved?.selectedTierDiscountCode?.() || '';
        if (offersEngraved?.applyDiscountCode) {
          await offersEngraved.applyDiscountCode(code);
        }
        const cart = await api.addItem(
          bundlesEngraved.length > 0
            ? { items: [engravedPayload, ...bundlesEngraved] }
            : engravedPayload
        );
        if (code && offersEngraved?.applyDiscountCode) {
          await offersEngraved.applyDiscountCode(code);
        }
        const fresh = code && api.getCart ? await api.getCart() : cart;
        await api.onCartUpdated(fresh || cart, { openDrawer: true });
        showSuccess(qs(root, '[data-engraving-success]'));
        window.setTimeout(() => {
          clearProductForm();
          delete root.dataset.engravingRebound;
        }, 1600);
      });
    });
  }

  function assertLineKey(key) {
    if (!key || typeof key !== 'string') {
      throw new Error('Missing Shopify line item key. Personalization cannot be updated.');
    }
    return key;
  }

  function init() {
    // Dialogs stay in <template> until first open — do not materialize on load.
    document.addEventListener('click', onOpenClick);
    document.addEventListener('click', onRemoveClick);
    document.addEventListener('click', onClearSavedClick);
    document.addEventListener('keydown', onKeydownTrap);
    bindVariantChange();
    interceptProductForm();
  }

  // Test / debug hooks — sanitizers only (no DOM). Theme UI never uses innerHTML for user text.
  window.NimizoEngravingValidation = {
    sanitizeText: sanitizeEngravingText,
    sanitizeEmoji: sanitizeEngravingEmoji,
    normalizeDraft,
    graphemeLength,
    sliceGraphemes,
    formatDisplay,
    allowedEmojis: EMOJIS.slice(),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
